import { istDate, istToday } from "@/lib/istDate";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { API, getAuthToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import OrderDetailDialog from "@/components/OrderDetailDialog";
import {
  ChartBar, DownloadSimple, CurrencyInr, Users, Warning,
  UserCircle, Trophy, ArrowClockwise, ChartLineUp, MagnifyingGlass, ArrowRight,
  ShieldCheck, FileArrowDown, Scissors, CheckCircle, Pencil, FilePdf
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from "recharts";

const REPORTS = [
  { key: "sales", label: "All Sales", icon: ChartBar },
  { key: "sales-sheet", label: "Sheet Cutting Sales", icon: Scissors },
  { key: "sales-gate", label: "Gate & Railing Sales", icon: ChartBar },
  { key: "payments", label: "Payments", icon: CurrencyInr },
  { key: "outstanding", label: "Outstanding", icon: Warning },
  { key: "profit-loss", label: "Profit & Loss", icon: ChartLineUp },
  { key: "staff", label: "Staff", icon: UserCircle },
  { key: "customers", label: "Top Customers", icon: Trophy },
  { key: "staff-activity", label: "Staff Activity", icon: ShieldCheck },
];

// Format Date as local YYYY-MM-DD (avoids UTC shift that makes 1st appear as previous month's 31st for IST users)
const SALES_PT = { "sales-sheet": "sheet_cutting", "sales-gate": "gate_railing" };
const reportPath = (key) => (SALES_PT[key] ? "sales" : key);
const ptParam = (key) => (SALES_PT[key] ? `&project_type=${SALES_PT[key]}` : "");

const localIso = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return istDate(d);
};

/**
 * Format a payment ISO timestamp for the reports table.
 *
 * Many historical entries were bulk-migrated with `T00:00:00Z` (midnight UTC)
 * — rendering these with `.toLocaleString()` in IST prints a misleading
 * "5:30:00 pm" because +5:30 offset makes 00:00 UTC → 05:30 IST. If the
 * original UTC time is exactly 00:00:00 (i.e., date-only entry), we show
 * date-only so the owner isn't confused. Entries with a real recorded time
 * (from POST /orders/{id}/payment which uses `now_iso()`) still show
 * "14/8/2026, 1:57 pm" etc.
 */
function fmtPaymentDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const isMidnightUtc =
      d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
    if (isMidnightUtc) {
      // Date-only — render in the viewer's local calendar day.
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
    }
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Default range: first day of current month → today
function defaultRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: localIso(start), to: localIso(now) };
}

export default function ReportsPage() {
  const [range, setRange] = useState(defaultRange());
  const [active, setActive] = useState("sales");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setData(null);
    try {
      const params = active === "outstanding"
        ? ""
        : `?from=${range.from}&to=${range.to}${ptParam(active)}`;
      const { data } = await api.get(`/reports/${reportPath(active)}${params}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  }, [active, range]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    const tok = getAuthToken() || "";
    const q = active === "outstanding"
      ? `?auth=${encodeURIComponent(tok)}`
      : `?from=${range.from}&to=${range.to}${ptParam(active)}&auth=${encodeURIComponent(tok)}`;
    window.open(`${API}/reports/export/${reportPath(active)}.csv${q}`, "_blank");
  };

  const exportPDF = () => {
    const tok = getAuthToken() || "";
    const q = active === "outstanding"
      ? `?auth=${encodeURIComponent(tok)}`
      : `?from=${range.from}&to=${range.to}${ptParam(active)}&auth=${encodeURIComponent(tok)}`;
    window.open(`${API}/reports/export/${reportPath(active)}.pdf${q}`, "_blank");
  };

  const setPreset = (preset) => {
    const now = new Date();
    const iso = localIso;
    if (preset === "today") {
      const d = iso(now);
      setRange({ from: d, to: d });
    } else if (preset === "week") {
      const from = new Date(now); from.setDate(now.getDate() - 6);
      setRange({ from: iso(from), to: iso(now) });
    } else if (preset === "month") {
      setRange({ from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) });
    } else if (preset === "last_month") {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      setRange({ from: iso(from), to: iso(to) });
    } else if (preset === "year") {
      setRange({ from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) });
    }
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// REPORTS</div>
          <h1 className="font-heading text-3xl font-black text-white">Business Reports</h1>
          <p className="text-slate-400 text-sm mt-1">
            Track sales, payments, outstanding dues and staff performance. Export any report as PDF or CSV for Tally / Excel.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={exportPDF}
            variant="outline"
            className="rounded-sm h-10 border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
            data-testid="export-pdf-button"
          >
            <FilePdf size={16} className="mr-1" weight="bold" /> EXPORT PDF
          </Button>
          <Button
            onClick={exportCSV}
            className="btn-industrial rounded-sm h-10"
            data-testid="export-csv-button"
          >
            <DownloadSimple size={16} className="mr-1" weight="bold" /> EXPORT CSV
          </Button>
        </div>
      </div>

      {/* Date range filter (hidden for Outstanding which is real-time) */}
      {active !== "outstanding" && (
        <div className="surface p-4 rounded-sm flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">FROM</Label>
              <Input
                type="date"
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num mt-1 w-[150px] sm:w-auto"
                data-testid="report-from-input"
              />
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">TO</Label>
              <Input
                type="date"
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm font-mono-num mt-1 w-[150px] sm:w-auto"
                data-testid="report-to-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap self-end">
            <PresetBtn label="Today" onClick={() => setPreset("today")} />
            <PresetBtn label="7 Days" onClick={() => setPreset("week")} />
            <PresetBtn label="This Month" onClick={() => setPreset("month")} />
            <PresetBtn label="Last Month" onClick={() => setPreset("last_month")} />
            <PresetBtn label="This Year" onClick={() => setPreset("year")} />
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="ml-auto rounded-sm border-slate-600 text-slate-200 hover:bg-slate-800 h-9 self-end"
            data-testid="reload-report-button"
          >
            <ArrowClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      )}

      {/* Report tabs */}
      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="bg-slate-800/60 border border-slate-700 rounded-sm h-auto min-h-11 flex-wrap justify-start">
          {REPORTS.map((r) => {
            const Icon = r.icon;
            return (
              <TabsTrigger
                key={r.key} value={r.key}
                className="data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-sm h-9 font-mono-num tracking-widest text-xs"
                data-testid={`report-tab-${r.key}`}
              >
                <Icon size={13} className="mr-1.5" /> {r.label.toUpperCase()}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {["sales", "sales-sheet", "sales-gate"].map((k) => (
          <TabsContent key={k} value={k} className="mt-5">
            {busy ? <Loading /> : <SalesReport data={data} showSplit={k === "sales"} />}
          </TabsContent>
        ))}
        <TabsContent value="payments" className="mt-5">
          {busy ? <Loading /> : <PaymentsReport data={data} onReload={load} />}
        </TabsContent>
        <TabsContent value="outstanding" className="mt-5">
          {busy ? <Loading /> : <OutstandingReport data={data} />}
        </TabsContent>
        <TabsContent value="staff" className="mt-5">
          {busy ? <Loading /> : <StaffReport data={data} />}
        </TabsContent>
        <TabsContent value="customers" className="mt-5">
          {busy ? <Loading /> : <CustomersReport data={data} />}
        </TabsContent>
        <TabsContent value="profit-loss" className="mt-5">
          {busy ? <Loading /> : <ProfitLossReport data={data} />}
        </TabsContent>
        <TabsContent value="staff-activity" className="mt-5">
          <StaffActivityReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PresetBtn({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 text-[11px] font-mono-num tracking-widest text-slate-300 bg-slate-800/60 hover:bg-slate-700 border border-slate-700 rounded-sm"
    >
      {label.toUpperCase()}
    </button>
  );
}

function Loading() {
  return <div className="surface p-16 rounded-sm text-center text-slate-500 font-mono-num">Loading report…</div>;
}

function KpiCard({ label, value, tone = "orange" }) {
  const toneMap = {
    orange: "text-orange-400 border-orange-500/40",
    cyan: "text-cyan-300 border-cyan-500/40",
    emerald: "text-emerald-400 border-emerald-500/40",
    red: "text-red-400 border-red-500/40",
  };
  return (
    <div className={`surface p-4 rounded-sm border-t-2 ${toneMap[tone]}`}>
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400">{label.toUpperCase()}</div>
      <div className={`mt-2 font-heading text-lg sm:text-3xl font-black font-mono-num whitespace-nowrap ${toneMap[tone].split(" ")[0]}`}>{value}</div>
    </div>
  );
}

/* ---------- Sales ---------- */
function SalesReport({ data, showSplit }) {
  if (!data || !Array.isArray(data.orders)) return <Loading />;
  const bp = data.by_project || {};
  const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="Orders" value={data.total_orders} tone="cyan" />
        <KpiCard label="Total Amount" value={fmt(data.total_amount)} />
        <KpiCard label="Total SqFt" value={data.total_sqft} tone="emerald" />
      </div>
      {showSplit && (
        <div className="grid grid-cols-2 gap-3" data-testid="sales-split-cards">
          <div data-testid="sales-split-sheet">
            <KpiCard label={`Sheet Cutting · ${bp.sheet_cutting?.orders || 0} orders`} value={fmt(bp.sheet_cutting?.amount)} />
          </div>
          <div data-testid="sales-split-gate">
            <KpiCard label={`Gate & Railing · ${bp.gate_railing?.orders || 0} orders`} value={fmt(bp.gate_railing?.amount)} tone="cyan" />
          </div>
        </div>
      )}

      {data.daily?.length > 0 && (
        <div className="surface p-5 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-4">// DAILY REVENUE</div>
          <div className="w-full h-56">
            <ResponsiveContainer>
              <BarChart data={data.daily} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#334155" vertical={false} />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11}
                  tick={{ fill: "#94A3B8", fontFamily: "'JetBrains Mono', monospace" }}
                  tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#64748B" fontSize={11}
                  tick={{ fill: "#94A3B8", fontFamily: "'JetBrains Mono', monospace" }}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <Tooltip
                  cursor={{ fill: "rgba(255,87,34,0.08)" }}
                  contentStyle={{
                    background: "#1E293B", border: "1px solid #475569",
                    borderRadius: 2, color: "#F8FAFC",
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                  }}
                  formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, "Revenue"]} />
                <Bar dataKey="amount" fill="#FF5722" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="surface rounded-sm overflow-hidden">
        <div className="px-5 py-3 text-xs font-mono-num tracking-widest text-slate-400 border-b border-slate-700">
          // ORDERS ({data.orders.length})
        </div>
        {data.orders.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No orders in this date range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-500 font-mono-num text-[11px] tracking-widest bg-slate-800/50">
                <tr>
                  <th className="text-left px-4 py-3">ORDER #</th>
                  <th className="text-left">CUSTOMER</th>
                  <th className="text-right">SQFT</th>
                  <th className="text-right">TOTAL</th>
                  <th className="text-right">PAID</th>
                  <th className="text-right px-4">BALANCE</th>
                  <th className="text-left">STATUS</th>
                  <th className="text-left">BY</th>
                  <th className="text-right px-4">DATE</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((r) => (
                  <tr key={r.order_number} className="border-t border-slate-800">
                    <td className="px-4 py-2.5 font-mono-num text-orange-400">#{r.order_number}</td>
                    <td className="text-slate-200">{r.customer_name}</td>
                    <td className="text-right font-mono-num text-cyan-300">{r.total_sqft}</td>
                    <td className="text-right font-mono-num">₹{r.total_amount.toLocaleString("en-IN")}</td>
                    <td className="text-right font-mono-num text-emerald-400">₹{r.advance_paid.toLocaleString("en-IN")}</td>
                    <td className="text-right font-mono-num text-orange-400 px-4">₹{r.balance.toLocaleString("en-IN")}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="text-slate-400 text-xs">{r.created_by_name || "—"}</td>
                    <td className="text-right px-4 text-xs font-mono-num text-slate-500">
                      {(r.created_at || "").slice(0, 10)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Payments ---------- */
function PaymentsReport({ data, onReload }) {
  const [editing, setEditing] = useState(null);
  const [modes, setModes] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data: m } = await api.get("/settings/payment-modes");
        setModes(Array.isArray(m) ? m : []);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  if (!data || !Array.isArray(data.payments)) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="Total Received" value={`₹${data.total.toLocaleString("en-IN")}`} tone="emerald" />
        <KpiCard label="Transactions" value={data.payments.length} tone="cyan" />
        <KpiCard label="Modes Used" value={data.by_mode.length} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="surface p-5 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-3">// BY PAYMENT MODE</div>
          {data.by_mode.length === 0 ? (
            <div className="text-slate-500 text-sm">No payments in range.</div>
          ) : (
            <div className="space-y-2">
              {data.by_mode.map((m) => {
                const pct = data.total > 0 ? (m.amount / data.total) * 100 : 0;
                return (
                  <div key={m.key}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-200">{m.label}</span>
                      <span className="font-mono-num text-orange-400">
                        ₹{m.amount.toLocaleString("en-IN")}{" "}
                        <span className="text-slate-500 text-xs">({m.count})</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-sm overflow-hidden">
                      <div className="h-full bg-orange-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="surface p-5 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-3">// BY STAFF</div>
          {data.by_staff.length === 0 ? (
            <div className="text-slate-500 text-sm">No payments in range.</div>
          ) : (
            <div className="space-y-2">
              {data.by_staff.map((s) => (
                <div key={s.name} className="flex items-center justify-between p-2 rounded-sm bg-slate-800/40">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-sm bg-orange-500/20 border border-orange-500/40 flex items-center justify-center text-orange-400 text-xs font-bold">
                      {(s.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className="text-slate-200 text-sm">{s.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-num text-cyan-300 text-sm">₹{s.amount.toLocaleString("en-IN")}</div>
                    <div className="text-[10px] font-mono-num text-slate-500">{s.count} txn</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="surface rounded-sm overflow-hidden">
        <div className="px-5 py-3 text-xs font-mono-num tracking-widest text-slate-400 border-b border-slate-700">
          // ALL PAYMENTS ({data.payments.length})
        </div>
        {data.payments.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No payments in this date range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-500 font-mono-num text-[11px] tracking-widest bg-slate-800/50">
                <tr>
                  <th className="text-left px-4 py-3">DATE</th>
                  <th className="text-left">ORDER #</th>
                  <th className="text-left">CUSTOMER</th>
                  <th className="text-right">AMOUNT</th>
                  <th className="text-left px-4">MODE</th>
                  <th className="text-left">BY</th>
                  <th className="text-left">NOTE</th>
                  <th className="text-right px-4 w-16">EDIT</th>
                </tr>
              </thead>
              <tbody>
                {data.payments.map((r, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="px-4 py-2.5 font-mono-num text-xs text-slate-400">
                      {fmtPaymentDate(r.date)}
                    </td>
                    <td className="font-mono-num text-orange-400">#{r.order_number}</td>
                    <td className="text-slate-200">{r.customer_name}</td>
                    <td className="text-right font-mono-num text-emerald-400">
                      ₹{r.amount.toLocaleString("en-IN")}
                    </td>
                    <td className="text-slate-300 px-4">{r.mode_label}</td>
                    <td className="text-slate-400 text-xs">{r.recorded_by_name}</td>
                    <td className="text-slate-500 text-xs">{r.note || "—"}</td>
                    <td className="text-right px-4">
                      <button
                        onClick={() => setEditing(r)}
                        className="text-slate-500 hover:text-orange-400 p-1"
                        data-testid={`edit-payment-${r.payment_id}`}
                        title="Edit payment mode / note"
                        disabled={!r.order_id || !r.payment_id}
                      >
                        <Pencil size={14} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EditPaymentDialog
        open={!!editing}
        payment={editing}
        modes={modes}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onReload?.(); }}
      />
    </div>
  );
}

function EditPaymentDialog({ open, payment, modes, onClose, onSaved }) {
  const [mode, setMode] = useState("");
  const [note, setNote] = useState("");
  const [dateStr, setDateStr] = useState(""); // YYYY-MM-DD (local)
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && payment) {
      setMode(payment.mode || "");
      setNote(payment.note || "");
      // Derive YYYY-MM-DD in the viewer's LOCAL time so "when I recorded it"
      // matches what the owner sees — not the UTC date part.
      try {
        const d = new Date(payment.date);
        const pad = (n) => String(n).padStart(2, "0");
        setDateStr(istDate(d));
      } catch { setDateStr(""); }
    }
  }, [open, payment]);

  const save = async () => {
    if (!mode) { toast.error("Select a mode"); return; }
    // Was original date in local time?
    let origDate = "";
    try {
      const d = new Date(payment.date);
      const pad = (n) => String(n).padStart(2, "0");
      origDate = istDate(d);
    } catch {}
    const dateChanged = dateStr && dateStr !== origDate;
    if (mode === payment.mode && (note || "") === (payment.note || "") && !dateChanged) {
      toast.info("Nothing changed"); onClose(); return;
    }
    setBusy(true);
    try {
      const payload = { mode, note };
      if (dateChanged) payload.created_at = dateStr;
      await api.patch(`/orders/${payment.order_id}/payments/${payment.payment_id}`, payload);
      toast.success("Payment updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  if (!payment) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm" data-testid="edit-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Pencil size={18} className="text-orange-400" weight="fill" /> Edit Payment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-sm bg-slate-800/60 border border-slate-700 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-400 text-xs">Order</span>
              <span className="font-mono-num text-orange-400">#{payment.order_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 text-xs">Customer</span>
              <span className="text-slate-100 truncate max-w-[200px]">{payment.customer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 text-xs">Amount</span>
              <span className="font-mono-num text-emerald-400 font-bold">
                ₹{Number(payment.amount || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div className="text-[10px] text-slate-500 pt-1 border-t border-slate-700 mt-1">
              Amount edit nahi ho sakta — sirf mode, date aur note.
            </div>
          </div>

          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">PAYMENT DATE</Label>
            <Input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              className="mt-1 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
              data-testid="edit-payment-date-input"
            />
            <div className="text-[10px] text-slate-500 mt-1">
              Galti se aaj ka date ho gaya? Yahaan sahi date daaliye — party ne jab actually diya tha.
            </div>
          </div>

          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="mt-1 w-full bg-slate-800/60 border border-slate-600 hover:border-orange-500/60 focus:border-orange-500 focus:outline-none text-white h-10 rounded-sm font-mono-num px-2 text-sm"
              data-testid="edit-payment-mode-select"
            >
              {modes.length === 0 ? (
                <option value={payment.mode}>{payment.mode_label}</option>
              ) : (
                modes.map((m) => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))
              )}
            </select>
            <div className="text-[10px] text-slate-500 mt-1">
              Was: <span className="text-orange-300">{payment.mode_label}</span>
            </div>
          </div>

          <div>
            <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE (OPTIONAL)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., corrected from cash → HDFC"
              className="mt-1 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
              data-testid="edit-payment-note-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={busy}
              className="rounded-sm bg-orange-500 hover:bg-orange-400 text-white font-heading tracking-widest px-5"
              data-testid="edit-payment-save"
            >
              {busy ? "SAVING…" : "SAVE"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Outstanding ---------- */
function OutstandingReport({ data }) {
  if (!data || !Array.isArray(data.customers)) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="Total Receivable" value={`₹${data.grand_total.toLocaleString("en-IN")}`} tone="red" />
        <KpiCard label="Customers Owe" value={data.customer_count} tone="orange" />
        <KpiCard label="Open Orders" value={data.order_count} tone="cyan" />
      </div>

      {data.customers.length === 0 ? (
        <div className="surface p-12 rounded-sm text-center">
          <div className="font-heading text-xl text-emerald-400 font-black">All Clear ✓</div>
          <div className="text-slate-400 mt-1">No pending payments right now.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {data.customers.map((c) => (
            <div key={c.mobile || c.customer_name} className="surface p-4 rounded-sm">
              <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-slate-200 font-semibold">{c.customer_name}</div>
                  <div className="font-mono-num text-cyan-300 text-xs">
                    <Users size={11} className="inline mr-1" /> {c.mobile}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">TOTAL PENDING</div>
                  <div className="font-mono-num text-2xl font-black text-orange-400">
                    ₹{c.total_pending.toLocaleString("en-IN")}
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                {c.orders.map((o) => (
                  <div key={o.order_number}
                    className="flex items-center justify-between p-2 rounded-sm bg-slate-800/40 text-sm">
                    <span className="font-mono-num text-orange-400">#{o.order_number}</span>
                    <StatusBadge status={o.status} />
                    <span className="font-mono-num text-slate-300">
                      ₹{o.balance.toLocaleString("en-IN")}
                    </span>
                    <span className={
                      "text-xs font-mono-num px-2 py-0.5 rounded-sm border " +
                      (o.age_days >= 30
                        ? "text-red-400 border-red-500/40 bg-red-500/10"
                        : o.age_days >= 14
                        ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
                        : "text-slate-400 border-slate-700 bg-slate-800")
                    }>
                      {o.age_days}d old
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Staff ---------- */
function StaffReport({ data }) {
  if (!data || !Array.isArray(data.staff)) return <Loading />;
  if (data.staff.length === 0) {
    return <div className="surface p-12 rounded-sm text-center text-slate-500">No staff activity in this date range.</div>;
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-3">
        {data.staff.map((s, i) => (
          <div key={s.name} className="surface p-4 rounded-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className={
                "w-10 h-10 rounded-sm flex items-center justify-center text-lg font-bold border-2 " +
                (i === 0 ? "bg-orange-500/20 border-orange-500 text-orange-400"
                  : "bg-slate-800 border-slate-700 text-slate-300")
              }>
                {(s.name || "?").charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-slate-200 font-semibold">{s.name}</div>
                {i === 0 && (
                  <div className="text-[10px] font-mono-num text-orange-400 tracking-widest">
                    <Trophy size={10} className="inline" /> TOP PERFORMER
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniMetric label="Orders Created" value={s.orders_created} tone="cyan" />
              <MiniMetric label="Order Value" value={`₹${s.orders_amount.toLocaleString("en-IN")}`} />
              <MiniMetric label="Payments Logged" value={s.payments_recorded} tone="cyan" />
              <MiniMetric label="Payment Value" value={`₹${s.payments_amount.toLocaleString("en-IN")}`} tone="emerald" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniMetric({ label, value, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-200",
    cyan: "text-cyan-300",
    orange: "text-orange-400",
    emerald: "text-emerald-400",
  };
  return (
    <div className="surface-2 px-3 py-2 rounded-sm">
      <div className="text-[9px] font-mono-num text-slate-500 tracking-widest">{label.toUpperCase()}</div>
      <div className={`font-mono-num text-lg font-semibold ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}

/* ---------- Customers ---------- */
function CustomersReport({ data }) {
  if (!data || !Array.isArray(data.customers)) return <Loading />;
  if (data.customers.length === 0) {
    return <div className="surface p-12 rounded-sm text-center text-slate-500">No customer activity in this date range.</div>;
  }
  return (
    <div className="surface rounded-sm overflow-hidden">
      <div className="px-5 py-3 text-xs font-mono-num tracking-widest text-slate-400 border-b border-slate-700">
        // TOP {data.customers.length} CUSTOMERS BY REVENUE
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-slate-500 font-mono-num text-[11px] tracking-widest bg-slate-800/50">
            <tr>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left">CUSTOMER</th>
              <th className="text-left">MOBILE</th>
              <th className="text-right">ORDERS</th>
              <th className="text-right">SQFT</th>
              <th className="text-right">REVENUE</th>
              <th className="text-right">BALANCE</th>
              <th className="text-right px-4">LAST ORDER</th>
            </tr>
          </thead>
          <tbody>
            {data.customers.map((r, i) => (
              <tr key={r.mobile || r.customer_name} className="border-t border-slate-800">
                <td className="px-4 py-2.5 font-mono-num text-slate-500">{i + 1}</td>
                <td className="text-slate-200 font-medium">{r.customer_name}</td>
                <td className="text-cyan-300 font-mono-num text-xs">{r.mobile}</td>
                <td className="text-right font-mono-num">{r.orders}</td>
                <td className="text-right font-mono-num text-cyan-300">{r.sqft}</td>
                <td className="text-right font-mono-num text-orange-400 font-semibold">
                  ₹{r.amount.toLocaleString("en-IN")}
                </td>
                <td className="text-right font-mono-num">₹{r.balance.toLocaleString("en-IN")}</td>
                <td className="text-right px-4 text-xs font-mono-num text-slate-500">
                  {(r.last_order || "").slice(0, 10)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Profit & Loss ---------- */
function ProfitLossReport({ data }) {
  if (!data || data.revenue === undefined || data.gross_profit === undefined) return <Loading />;
  const isProfit = data.gross_profit >= 0;
  const cogs = data.cogs !== undefined ? data.cogs : data.purchase_cost;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Revenue (Sales)" value={`₹${data.revenue.toLocaleString("en-IN")}`} tone="cyan" />
        <KpiCard label="Cash Received" value={`₹${data.received.toLocaleString("en-IN")}`} tone="emerald" />
        <KpiCard label="COGS (Material Cost)" value={`₹${cogs.toLocaleString("en-IN")}`} tone="red" />
        <KpiCard label={isProfit ? "Gross Profit" : "Gross Loss"}
          value={`₹${Math.abs(data.gross_profit).toLocaleString("en-IN")}`}
          tone={isProfit ? "emerald" : "red"} />
      </div>

      <div className="surface p-6 rounded-sm">
        <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-3">// SUMMARY</div>
        <div className="space-y-4">
          <PLRow label="Revenue from Orders" value={data.revenue} tone="cyan" />
          <PLRow label="- Cost of Goods Sold (purchase_price × sqft / qty)" value={-cogs} tone="red" />
          <div className="border-t border-slate-700 pt-4">
            <div className="flex items-center justify-between">
              <span className={"font-heading text-xl " + (isProfit ? "text-emerald-400" : "text-red-400")}>
                {isProfit ? "GROSS PROFIT" : "GROSS LOSS"}
              </span>
              <span className={
                "font-mono-num text-3xl font-black " + (isProfit ? "text-emerald-400" : "text-red-400")
              }>
                ₹{Math.abs(data.gross_profit).toLocaleString("en-IN")}
              </span>
            </div>
            {data.revenue > 0 && (
              <div className="mt-2 text-right text-sm font-mono-num text-slate-400">
                Margin: <b className={isProfit ? "text-emerald-400" : "text-red-400"}>{data.margin_pct}%</b>
              </div>
            )}
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-slate-800 text-sm">
          <div className="text-slate-500">
            Orders in range: <span className="text-slate-200 font-mono-num">{data.order_count}</span>
          </div>
        </div>
        <div className="mt-4 text-[11px] font-mono-num text-slate-500 border-t border-slate-800 pt-3 leading-relaxed">
          <b className="text-slate-300">How profit is computed:</b> For every order item, cost = <b>item purchase_price × sqft (or qty)</b>.
          Profit = Sale Amount − COGS. Example: sold @ ₹250/sqft, bought @ ₹120/sqft → profit ₹130/sqft.
          Set correct <b>Purchase Price</b> on each item in the <b>Items</b> page for accurate reporting.
        </div>
        <div className="mt-3 text-[11px] text-slate-500 border border-slate-700 rounded-sm p-2">
          Note: this is a <b>gross</b> P&amp;L — labour, rent, electricity and other overheads are not included.
        </div>
      </div>

      <BillWiseProfit orders={data.orders || []} />
    </div>
  );
}

/* ---------- Bill-wise / Order-wise profit table ---------- */
function BillWiseProfit({ orders }) {
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("asc");
  const [openId, setOpenId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = orders.filter((o) => {
      if (!needle) return true;
      return (
        (o.customer_name || "").toLowerCase().includes(needle) ||
        (o.order_number || "").toLowerCase().includes(needle) ||
        (o.mobile || "").includes(needle)
      );
    });
    rows = [...rows].sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case "profit": av = a.profit_amount; bv = b.profit_amount; break;
        case "total":  av = a.total_amount;  bv = b.total_amount;  break;
        case "party":  av = (a.customer_name || "").toLowerCase(); bv = (b.customer_name || "").toLowerCase(); break;
        default:       av = a.date; bv = b.date;
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }, [orders, q, sortBy, sortDir]);

  const totalSale = filtered.reduce((s, o) => s + (o.total_amount || 0), 0);
  const totalProfit = filtered.reduce((s, o) => s + (o.profit_amount || 0), 0);

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  const setSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "date" ? "asc" : "desc"); }
  };

  const sortArrow = (key) => sortBy === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  return (
    <div className="surface rounded-sm overflow-hidden" data-testid="bill-wise-profit">
      <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// PROFIT ON SALE INVOICES</div>
          <div className="text-slate-100 font-heading text-lg mt-0.5">Bill-wise Profit ({filtered.length})</div>
        </div>
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by customer / #order / mobile…"
            className="pl-9 bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm w-72"
            data-testid="bill-wise-search"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="p-12 text-center text-slate-500 text-sm">No orders in this range.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-500 font-mono-num text-[10px] tracking-widest bg-slate-800/50 uppercase">
              <tr>
                <th className="text-left px-4 py-3 cursor-pointer hover:text-slate-300" onClick={() => setSort("date")}>
                  Date{sortArrow("date")}
                </th>
                <th className="text-left cursor-pointer hover:text-slate-300" onClick={() => setSort("date")}>
                  Order #
                </th>
                <th className="text-left cursor-pointer hover:text-slate-300" onClick={() => setSort("party")}>
                  Party{sortArrow("party")}
                </th>
                <th className="text-right pr-4 cursor-pointer hover:text-slate-300" onClick={() => setSort("total")}>
                  Total Sale{sortArrow("total")}
                </th>
                <th className="text-right pr-4">Material Cost</th>
                <th className="text-right pr-4 cursor-pointer hover:text-slate-300" onClick={() => setSort("profit")}>
                  Profit (+) / Loss (−){sortArrow("profit")}
                </th>
                <th className="text-right pr-4">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const isLoss = (o.profit_amount || 0) < 0;
                const dateStr = o.date ? new Date(o.date).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
                const isOpen = expanded.has(o.id);
                return (
                  <React.Fragment key={o.id}>
                    <tr className="border-t border-slate-800 hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-mono-num text-slate-400 text-xs">{dateStr}</td>
                      <td>
                        <button
                          onClick={() => setOpenId(o.id)}
                          className="text-orange-400 font-mono-num text-xs hover:underline"
                          data-testid={`bill-open-${o.order_number}`}
                        >
                          #{o.order_number}
                        </button>
                      </td>
                      <td className="text-slate-200">
                        <div className="truncate max-w-[220px]" title={o.customer_name}>{o.customer_name}</div>
                        <div className="text-[10px] font-mono-num text-slate-500">{o.mobile}</div>
                      </td>
                      <td className="text-right font-mono-num pr-4 text-slate-200">
                        ₹{(o.total_amount || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="text-right font-mono-num pr-4 text-red-400">
                        ₹{(o.cost_amount || 0).toLocaleString("en-IN")}
                      </td>
                      <td className={"text-right font-mono-num pr-4 font-bold " + (isLoss ? "text-red-400" : "text-emerald-400")}>
                        {isLoss ? "− " : ""}₹{Math.abs(o.profit_amount || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="text-right pr-4">
                        <button
                          onClick={() => toggleExpand(o.id)}
                          className="text-cyan-300 text-xs font-mono-num hover:underline"
                          data-testid={`bill-toggle-${o.order_number}`}
                        >
                          {isOpen ? "Hide" : "Show"} <ArrowRight size={10} className="inline"/>
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-slate-800 bg-slate-900/60">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mb-2">
                            // ITEM BREAKDOWN
                          </div>
                          <table className="w-full text-xs">
                            <thead className="text-slate-500">
                              <tr>
                                <th className="text-left py-1">Item</th>
                                <th className="text-right">Qty / SqFt</th>
                                <th className="text-right">Sale Rate</th>
                                <th className="text-right">Cost Rate</th>
                                <th className="text-right">Sale ₹</th>
                                <th className="text-right">Cost ₹</th>
                                <th className="text-right">Profit ₹</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(o.items || []).map((it, i) => {
                                const qtyDisplay = it.unit === "sqft"
                                  ? `${it.total_sqft} sqft`
                                  : `${it.quantity} ${it.unit}`;
                                const itemLoss = (it.profit_amount || 0) < 0;
                                return (
                                  <tr key={i} className="border-t border-slate-800/60">
                                    <td className="py-1.5 text-slate-300">{it.name || "—"}</td>
                                    <td className="text-right font-mono-num text-slate-400">{qtyDisplay}</td>
                                    <td className="text-right font-mono-num text-slate-300">₹{it.sale_rate}</td>
                                    <td className="text-right font-mono-num text-slate-400">₹{it.cost_rate}</td>
                                    <td className="text-right font-mono-num text-cyan-300">
                                      ₹{(it.sale_amount || 0).toLocaleString("en-IN")}
                                    </td>
                                    <td className="text-right font-mono-num text-red-400">
                                      ₹{(it.cost_amount || 0).toLocaleString("en-IN")}
                                    </td>
                                    <td className={"text-right font-mono-num font-semibold " + (itemLoss ? "text-red-400" : "text-emerald-400")}>
                                      {itemLoss ? "− " : ""}₹{Math.abs(it.profit_amount || 0).toLocaleString("en-IN")}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-600 bg-slate-800/60">
                <td colSpan={3} className="px-4 py-3 font-heading text-slate-300 tracking-widest text-xs">
                  SUMMARY
                </td>
                <td className="text-right font-mono-num pr-4 text-cyan-300 font-bold">
                  ₹{totalSale.toLocaleString("en-IN")}
                </td>
                <td></td>
                <td className={"text-right font-mono-num pr-4 font-bold text-base " + (totalProfit >= 0 ? "text-emerald-400" : "text-red-400")}>
                  {totalProfit < 0 ? "− " : ""}₹{Math.abs(totalProfit).toLocaleString("en-IN")}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <OrderDetailDialog
        orderId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
        onUpdated={() => {}}
      />
    </div>
  );
}

function PLRow({ label, value, tone }) {
  const toneMap = { cyan: "text-cyan-300", red: "text-red-400", emerald: "text-emerald-400" };
  const positive = value >= 0;
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-300">{label}</span>
      <span className={"font-mono-num text-lg font-semibold " + (toneMap[tone] || "text-slate-200")}>
        {positive ? "" : "-"}₹{Math.abs(value).toLocaleString("en-IN")}
      </span>
    </div>
  );
}


/* -------------------- Staff Activity Report (admin only) --------------------
   Tracks which operator downloaded a cutting file, started cutting, and
   marked cutting done — so admin can trace who worked on which order in case
   of quality/pricing disputes.
*/
const KIND_META = {
  cutting_file_download: {
    label: "DOWNLOADED FILE",
    icon: FileArrowDown,
    tone: "text-cyan-300",
    bg: "bg-cyan-500/10 border-cyan-500/40",
  },
  cutting_started: {
    label: "CUTTING STARTED",
    icon: Scissors,
    tone: "text-orange-300",
    bg: "bg-orange-500/10 border-orange-500/40",
  },
  cutting_done: {
    label: "CUTTING DONE",
    icon: CheckCircle,
    tone: "text-emerald-300",
    bg: "bg-emerald-500/10 border-emerald-500/40",
  },
};

function StaffActivityReport() {
  const [rows, setRows] = useState([]);
  const [perStaff, setPerStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { limit: 500 };
      if (kindFilter !== "all") params.kind = kindFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/reports/staff-activity", { params });
      setRows(data.events || []);
      setPerStaff(data.per_staff || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, [kindFilter, q]);

  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="staff-activity-report">
      {/* Header */}
      <div className="surface p-4 rounded-sm flex items-center gap-3">
        <ShieldCheck size={24} className="text-orange-400" weight="fill" />
        <div className="flex-1">
          <div className="font-heading text-xl text-white">Staff Activity — Audit Trail</div>
          <div className="text-xs text-slate-400 mt-0.5">
            Har cutting file download, cutting start, aur cutting done ka record. Sirf admin ke liye — dispute hone par yahan check karo.
          </div>
        </div>
        <Button
          onClick={load}
          disabled={busy}
          className="rounded-sm h-9 bg-slate-800 hover:bg-slate-700 border border-slate-700"
          data-testid="staff-activity-refresh"
        >
          <ArrowClockwise size={14} className={busy ? "animate-spin mr-2" : "mr-2"} />
          Refresh
        </Button>
      </div>

      {/* Per-staff summary */}
      {perStaff.length > 0 && (
        <div className="surface p-4 rounded-sm">
          <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-3">// STAFF SUMMARY</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {perStaff.map((s) => (
              <div
                key={s.email}
                className="surface-2 rounded-sm p-3 border border-slate-800"
                data-testid={`staff-summary-${s.email}`}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-slate-100 font-semibold truncate">{s.name || s.email}</div>
                    <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest">
                      {s.role || "—"} · {s.email}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading font-black font-mono-num text-orange-400 text-2xl leading-none">
                      {s.total}
                    </div>
                    <div className="text-[9px] font-mono-num text-slate-500 tracking-widest">EVENTS</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3 text-[11px] font-mono-num">
                  <span className="text-cyan-300"><FileArrowDown size={10} className="inline mr-0.5" />{s.downloads}</span>
                  <span className="text-orange-300"><Scissors size={10} className="inline mr-0.5" />{s.cutting_started}</span>
                  <span className="text-emerald-300"><CheckCircle size={10} className="inline mr-0.5" />{s.cutting_done}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="surface p-4 rounded-sm flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">SEARCH</Label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Staff name, email, order #, customer…"
            className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
            data-testid="staff-activity-search"
          />
        </div>
        <div className="flex items-center gap-1 surface-2 p-1 rounded-sm">
          {[
            { k: "all", label: "ALL" },
            { k: "cutting_file_download", label: "DOWNLOADS" },
            { k: "cutting_started", label: "STARTED" },
            { k: "cutting_done", label: "DONE" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setKindFilter(f.k)}
              className={
                "px-3 py-1.5 rounded-sm text-[10px] font-mono-num tracking-widest transition-colors " +
                (kindFilter === f.k
                  ? "bg-orange-500 text-white"
                  : "text-slate-400 hover:text-slate-200")
              }
              data-testid={`staff-activity-filter-${f.k}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Events table */}
      <div className="surface rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-slate-500 font-mono-num text-[10px] tracking-widest bg-slate-900/60">
            <tr>
              <th className="text-left py-2 px-3">WHEN</th>
              <th className="text-left">ACTION</th>
              <th className="text-left">STAFF</th>
              <th className="text-left">ORDER</th>
              <th className="text-left">CUSTOMER</th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm">Loading…</td></tr>
            )}
            {!busy && rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm">
                Koi activity nahi mili. Jaise hi operator cutting file download / mark karega, log yahan aayega.
              </td></tr>
            )}
            {!busy && rows.map((r) => {
              const meta = KIND_META[r.kind] || {};
              const Icon = meta.icon;
              return (
                <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`staff-activity-row-${r.id}`}>
                  <td className="py-2.5 px-3 text-slate-400 font-mono-num text-xs whitespace-nowrap">{fmt(r.at)}</td>
                  <td>
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-mono-num tracking-widest px-2 py-0.5 rounded-sm border ${meta.bg} ${meta.tone}`}
                    >
                      {Icon && <Icon size={11} weight="bold" />} {meta.label || r.kind.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <div className="text-slate-100">{r.by_name || r.by || "—"}</div>
                    <div className="text-[10px] font-mono-num text-slate-500 uppercase">{r.by_role || ""}</div>
                  </td>
                  <td className="font-mono-num text-orange-400">#{r.order_number}</td>
                  <td className="text-slate-200 truncate max-w-[220px]">{r.customer_name || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


